import {
    Box,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from '@material-ui/core';
import { ArrowBack, Edit } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../../../components/Form/Button';
import { SeinoLogo } from '../../../components/Graphics/SeinoLogo';
import { useSeinoAuth } from '../../../hooks/use-seino-auth';
import { useSeinoStore } from '../../../store/seino-store';

export const ConfigureWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const Wrapper = styled.div`
    padding: 5rem 12rem;
`;

export const ConfigureActiveCampaign = () => {
    const navigate = useNavigate();
    const { fetchToken } = useSeinoAuth();
    const [error, setError] = useState(false);
    const [apiUrl, setApiUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [editingEnabled, setEditingEnabled] = useState(false);

    const {
        dataSource: {
            dataSourceConnectionsInitialized,
            configureActiveCampaign,
            hasActiveCampaignConnection,
            activeCampaignUrl,
            loading: { activeCampaign: loading },
        },
        user: { currentWorkspaceId },
    } = useSeinoStore();

    useEffect(() => {
        if (!dataSourceConnectionsInitialized) {
            navigate('/config/data-sources');
        }
    }, [dataSourceConnectionsInitialized, navigate]);

    const handleSubmit = async () => {
        if (!currentWorkspaceId) return;

        const token = await fetchToken();
        const result = await configureActiveCampaign(
            token,
            currentWorkspaceId,
            apiUrl,
            apiKey
        );

        if (result) {
            navigate('/config/data-sources');
        } else {
            setError(true);
        }
    };

    return (
        <ConfigureWrapper>
            <Wrapper>
                <SeinoLogo />
                <Typography my={4} variant="h4">
                    Connect your own data sources
                </Typography>
                <Box mt={4}>
                    <Divider />
                </Box>
                <Typography mt={1} mb={1} variant="h6">
                    Step 1 of 3: Configure your e-mail service provider
                </Typography>

                <Box mb={4}>
                    <Divider />
                </Box>

                <Typography style={{ maxWidth: 600 }}>
                    SEINō uses the ActiveCampaign REST API to retrieve data
                    about your campaigns. In order to access the API, we need
                    the URL and a API key associated with your ActiveCampaign
                    account. Please refer to{' '}
                    <Link
                        href="https://help.activecampaign.com/hc/en-us/articles/207317590-Getting-started-with-the-API#how-to-obtain-your-activecampaign-api-url-and-key"
                        target="_blank"
                    >
                        the ActiveCampaign documentation
                    </Link>{' '}
                    where to find this information.
                </Typography>

                {error && (
                    <Typography mt={2} color="error">
                        Connecting to ActiveCampaign failed. Please check the
                        URL and key you provided.
                    </Typography>
                )}

                <Box mt={3}>
                    <TextField
                        label="URL"
                        disabled={loading}
                        fullWidth={true}
                        value={apiUrl}
                        onFocus={e => e.target.select()}
                        onChange={e => setApiUrl(e.target.value)}
                        {...(hasActiveCampaignConnection && !editingEnabled
                            ? {
                                  disabled: true,
                                  value: activeCampaignUrl,
                                  InputProps: {
                                      endAdornment: (
                                          <InputAdornment position="end">
                                              <IconButton
                                                  onClick={() =>
                                                      setEditingEnabled(true)
                                                  }
                                                  edge="end"
                                                  sx={{ margin: 0 }}
                                              >
                                                  <Edit />
                                              </IconButton>
                                          </InputAdornment>
                                      ),
                                  },
                              }
                            : {})}
                    />
                </Box>
                <Box mt={3}>
                    <TextField
                        label="Key"
                        disabled={loading}
                        fullWidth={true}
                        value={apiKey}
                        onFocus={e => e.target.select()}
                        onChange={e => setApiKey(e.target.value)}
                        {...(hasActiveCampaignConnection && !editingEnabled
                            ? {
                                  disabled: true,
                                  value: '•'.repeat(64),
                                  InputProps: {
                                      endAdornment: (
                                          <InputAdornment position="end">
                                              <IconButton
                                                  onClick={() =>
                                                      setEditingEnabled(true)
                                                  }
                                                  edge="end"
                                                  sx={{ margin: 0 }}
                                              >
                                                  <Edit />
                                              </IconButton>
                                          </InputAdornment>
                                      ),
                                  },
                              }
                            : {})}
                    />
                </Box>

                <Grid
                    my={4}
                    container
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Grid item>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => {
                                navigate(
                                    '/config/data-sources/email-service-provider'
                                );
                            }}
                            variant="text"
                            color="primary"
                            title="Go back"
                        />
                    </Grid>

                    <Grid item>
                        <Button
                            disabled={loading || !apiKey || !apiUrl}
                            onClick={() => {
                                handleSubmit();
                            }}
                            fullWidth={false}
                            variant="contained"
                            title="Connect"
                            startIcon={
                                loading && <CircularProgress size={12} />
                            }
                        />
                    </Grid>
                </Grid>
            </Wrapper>
        </ConfigureWrapper>
    );
};
